import React from "react";
import { Button, OneLineList } from "../../../components/ui";
import { formatPrice } from "../../brand/planFormat";
import {
  type GateProps,
  type SubscriptionGate,
} from "../../billing/hooks/useSubscriptionGate";
import {
  EnquiryDetail,
  EnquiryPaymentDetail,
  EnquiryPaymentStatus,
  EnquiryStatus,
} from "../types/enquiries.types";

/** The same four tints the enquiries table uses, so a status reads the same on
 *  the row it was picked from and the record it opens. The border is the fill:
 *  these are flat pills, and the outlined ones no longer match the table. */
const STATUS_STYLES: Record<EnquiryStatus, string> = {
  "Recently Added": "border-[#EFFBFD] bg-[#EFFBFD] text-[#007E8C]",
  Interested: "border-[#ECFFEB] bg-[#ECFFEB] text-[#1A8810]",
  "Not interested": "border-[#F9F9FB] bg-[#F9F9FB] text-[#62636C]",
  "Payment sent": "border-[#FFF7D9] bg-[#FFF7D9] text-[#917A2E]",
  Converted: "border-[#E7F8EF] bg-[#E7F8EF] text-[#2F8E5B]",
};

/** Label followed by its value, the repeating unit of every card here. */
const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline gap-2">
    <span className="flex-shrink-0 text-[13px] 3xl:text-[14px] text-ink-muted">
      {label}:
    </span>
    {/* `min-w-0` so a value that measures itself against this cell — the goal
        line below — is given the cell's width rather than its own content's,
        which would never overflow and so never count anything. */}
    <span className="min-w-0 text-[14px] 3xl:text-[15px] font-medium text-ink">
      {value}
    </span>
  </div>
);

/**
 * A number with its dialling code. The code stays in the label's grey so the
 * value still reads as just the number.
 */
const DiallingCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <span className="font-normal text-ink-muted">+91</span> {children}
  </>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="rounded-md border border-line bg-white">{children}</section>
);

const PAYMENT_STATUS_STYLES: Record<EnquiryPaymentStatus, string> = {
  Pending: "border-[#F3D9A8] bg-[#FDF7EC] text-[#B27A16]",
  Paid: "border-[#BEE9CF] bg-[#F3FBF6] text-[#2F8E5B]",
  // The grey a called-off request wears in a client's payment history: nothing
  // is owed and nothing is coming, so it recedes rather than colouring.
  Cancelled: "border-line bg-[#F1F4F6] text-ink-muted",
};

/**
 * The payment requested from this enquiry — what stands where the Request
 * payment button was, once one has gone out.
 *
 * Confirming is offered while it is pending because nothing reports the money
 * in yet: a client who paid by UPI or in cash is marked off here. Declining is
 * the other answer, and the only way a request that is never going to be paid —
 * the wrong plan, the wrong price, an enquiry who changed their mind — stops
 * standing as money owed.
 */
const PaymentRow: React.FC<{
  payment: EnquiryPaymentDetail;
  /** Both answers, off together while the workspace has no plan behind it. */
  gate: GateProps;
  onConfirm: () => void;
  onDecline: () => void;
}> = ({ payment, gate, onConfirm, onDecline }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 px-5 sm:px-6 py-4">
    <div className="flex items-center gap-3">
      {/* The tile carries the currency, so the amount beside it is written as
          a plain number — a second ₹ an inch away would read as two prices. */}
      <span
        aria-hidden="true"
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#FDF0DC] text-[16px] font-medium text-[#C4881F]"
      >
        ₹
      </span>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="text-[18px] 3xl:text-[20px] font-medium text-ink"
            // What is written is the number; what is read out is the money,
            // and the plan it is for.
            aria-label={`${formatPrice(payment.amount)} for ${payment.planName}, ${payment.duration}`}
          >
            {payment.amount.toLocaleString("en-IN")}
          </span>
          {/* Smaller than the enquiry's own pill above it: this one labels the
              amount beside it rather than the record, and at the header's size
              it reads as the second status of the page. */}
          <span
            className={`inline-flex rounded-full border px-2 py-px text-[10px] font-medium leading-[13px] ${PAYMENT_STATUS_STYLES[payment.status]}`}
          >
            {payment.status}
          </span>
        </div>
        <div className="mt-0.5 text-[13px] 3xl:text-[14px] text-ink-muted">
          Requested on:{" "}
          <span className="font-medium text-ink">{payment.requestedOn}</span>
        </div>
      </div>
    </div>

    {/* Both answers together, the confirm leading: it is the one that happens,
        and the decline is the exception beside it rather than a choice of
        equals. Neither commits anything on its own — each opens its question
        first, because nothing takes either answer back. */}
    {payment.status === "Pending" && (
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onConfirm} {...gate}>
          Confirm payment
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onDecline}
          {...gate}
        >
          Payment declined
        </Button>
      </div>
    )}
  </div>
);

interface EnquiryDetailsViewProps {
  enquiry: EnquiryDetail;
  /**
   * Whether this workspace may still act on the enquiry, read once by the
   * screen above — see `EnquiryDetailPage`. Everything here writes: the record,
   * the stage it is at, and the money asked for it. The details themselves stay
   * readable, the way the roster and the list do.
   */
  gate: SubscriptionGate;
  /** Opens the Create new payment dialog. */
  onRequestPayment: () => void;
  /** Marks the requested payment as received. */
  onConfirmPayment: () => void;
  /** Calls the requested payment off — asked before anything is called off. */
  onDeclinePayment: () => void;
  /** Opens the edit form, filled with this enquiry's own answers. */
  onEdit: () => void;
}

const EnquiryDetailsView: React.FC<EnquiryDetailsViewProps> = ({
  enquiry,
  gate,
  onRequestPayment,
  onConfirmPayment,
  onDeclinePayment,
  onEdit,
}) => (
  <div className="flex flex-col gap-4">
    <Card>
      <div className="flex flex-wrap items-center gap-3 px-5 sm:px-6 py-5">
        <span className="w-11 h-11 rounded-full bg-[#DEEBFB] flex items-center justify-center flex-shrink-0">
          <span className="text-[16px] font-medium text-[#2F6BAE]">
            {enquiry.name.charAt(0)}
          </span>
        </span>
        <h2 className="text-[18px] sm:text-[20px] font-medium text-ink">
          {enquiry.salutation} {enquiry.name}
        </h2>
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[12px] font-medium ${STATUS_STYLES[enquiry.status]}`}
        >
          {enquiry.statusLabel}
        </span>

        {/* Pushed to the end of the row so it reads as an action on the header
            rather than another badge. A stage is moved from the list's row
            menu, so opening the whole record for editing is all this header
            offers. */}
        <div className="ml-auto flex items-center gap-3">
          <Button size="sm" onClick={onEdit} {...gate.props("edit an enquiry")}>
            Edit details
          </Button>
        </div>
      </div>

      {/* Spread edge to edge so the last field lines up with the card's right
          padding, the way the design has it. */}
      <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 border-t border-line px-5 sm:px-6 py-4">
        <Field label="Gender" value={enquiry.gender} />
        <Field label="DOB" value={enquiry.dob} />
        <Field label="Age" value={enquiry.age ?? "-"} />
        <Field label="Life style" value={enquiry.lifestyle} />
        <Field label="Profession" value={enquiry.profession} />
      </div>
    </Card>

    <Card>
      <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 px-5 sm:px-6 py-4">
        <Field
          label="Goal"
          value={<OneLineList items={enquiry.goals} label="goals" />}
        />
        <Field label="Requested via" value={enquiry.requestedVia} />
      </div>
    </Card>

    <Card>
      <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 px-5 sm:px-6 py-4">
        <Field label="Phone" value={<DiallingCode>{enquiry.phone}</DiallingCode>} />
        <Field
          label="WhatsApp"
          value={<DiallingCode>{enquiry.whatsapp}</DiallingCode>}
        />
        <Field label="Email" value={enquiry.email} />
      </div>
      <div className="border-t border-line px-5 sm:px-6 py-4">
        <div className="text-[13px] 3xl:text-[14px] text-ink-muted">
          Address:
        </div>
        <div className="mt-1 text-[14px] 3xl:text-[15px] font-medium text-ink">
          {enquiry.address}
        </div>
      </div>
    </Card>

    {/* The request replaces the button that raised it: there is one payment
        per enquiry, so an offer to raise a second beside the first would only
        ever be a mistake waiting to be clicked. */}
    <Card>
      {enquiry.payment ? (
        <PaymentRow
          payment={enquiry.payment}
          gate={gate.props("answer a payment")}
          onConfirm={onConfirmPayment}
          onDecline={onDeclinePayment}
        />
      ) : (
        <div className="px-5 sm:px-6 py-5">
          <Button
            size="sm"
            onClick={onRequestPayment}
            // "Payment sent" with no payment on the record is an enquiry moved
            // by hand from the list's row menu; there is nothing to show for
            // it, and nothing to raise either. That one stays plainly
            // disabled — its own label says why, so there is no press to
            // answer. Anything else wears the workspace's answer instead.
            {...(enquiry.status === "Payment sent"
              ? { disabled: true }
              : gate.props("request a payment"))}
          >
            {enquiry.status === "Payment sent" ? (
              "Payment requested"
            ) : (
              <>
                {/* Its own element rather than spaces in the label: a run of
                    them collapses to one space, which is not the gap the
                    design has. `mr-2` matches the `gap-2` the button gives a
                    real `startIcon`, so this sits like every other icon in the
                    app. Hidden from the accessibility tree — "plus Request
                    payment" is not what the button does. */}
                <span aria-hidden="true" className="mr-1">
                  +
                </span>
                Request payment
              </>
            )}
          </Button>
        </div>
      )}
    </Card>
  </div>
);

export default EnquiryDetailsView;
